import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, delay, map, of, throwError } from 'rxjs';

/** Plazo guardado en el mock (persiste en el navegador para probar «Guardar»). */
const STORAGE_KEY = 'waiis.mock.partner-payment-terms';
/** Días del calendario: el abono de hoy y los siguientes. */
const CALENDAR_DAYS = 10;

const TERMS_URL = /\/backoffice\/partners\/([^/]+)\/payment-terms(\?|$)/;
const CALENDAR_URL = /\/backoffice\/partners\/([^/]+)\/pending-payouts(\?|$)/;

interface StoredTerms {
  term_days: 30 | 60;
}

/**
 * Responde en el navegador «Condiciones de pago» del partner con la forma del
 * back (WS-2465) mientras el entorno no lo tenga. Solo se registra con
 * `environment.partnerPaymentsMock` (local). El resto de peticiones pasan tal cual.
 *
 * Contrato (importes en céntimos, fechas `YYYY-MM-DD`):
 * - `GET  partners/{id}/payment-terms` → `{ data: { term_days, scheduled_term_days, scheduled_from, options } }` (b2b)
 * - `PUT  partners/{id}/payment-terms` `{ term_days: 30 | 60 }` → lo mismo
 * - `GET  partners/{id}/pending-payouts` → `{ term_days, days: [{ date, payouts_count, amount_cents }] }` (money)
 *
 * Además pone `payment_terms_enabled: true` en el partner de la respuesta real
 * de `/me`, para que se vea la entrada del menú (en real lo decide el backend).
 */
@Injectable()
export class PartnerPaymentsMockInterceptor implements HttpInterceptor {
  constructor() {
    console.info('[PartnerPaymentsMock] Activo: plazo y calendario de abonos simulados.');
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.method === 'GET' && /\/backoffice\/me(\?|$)/.test(req.url)) {
      return next.handle(req).pipe(map((event) => this.withPaymentTerms(event)));
    }
    if (CALENDAR_URL.test(req.url) && req.method === 'GET') {
      return this.respond(this.calendar());
    }
    if (TERMS_URL.test(req.url)) {
      if (req.method === 'GET') return this.respond({ data: this.termsBody() });
      if (req.method === 'PUT') return this.updateTerms(req.body as any);
    }
    return next.handle(req);
  }

  private withPaymentTerms(event: HttpEvent<unknown>): HttpEvent<unknown> {
    if (!(event instanceof HttpResponse)) return event;
    const body: any = event.body;
    if (!body?.partner) return event;
    if (body.partner.payment_terms_enabled === true) return event;
    return event.clone({ body: { ...body, partner: { ...body.partner, payment_terms_enabled: true } } });
  }

  private updateTerms(body: any): Observable<HttpEvent<unknown>> {
    const days = Number(body?.term_days);
    if (days !== 30 && days !== 60) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 422,
            error: {
              message: 'El plazo debe ser de 30 o 60 días.',
              errors: { term_days: ['El plazo debe ser de 30 o 60 días.'] },
            },
          }),
      ).pipe(delay(400));
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ term_days: days } satisfies StoredTerms));
    } catch {
      // Sin almacenamiento: el cambio vale solo para esta respuesta.
    }
    return this.respond({ data: { ...this.termsBody(), term_days: days } });
  }

  private readTerms(): StoredTerms {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const stored = raw ? JSON.parse(raw) : null;
      if (stored?.term_days === 30 || stored?.term_days === 60) return stored;
    } catch {
      // Sin almacenamiento: plazo por defecto.
    }
    return { term_days: 30 };
  }

  private termsBody(): Record<string, unknown> {
    return { term_days: this.readTerms().term_days, scheduled_term_days: null, scheduled_from: null, options: [30, 60] };
  }

  /** Abonos pendientes desde hoy, por día. */
  private calendar(): unknown {
    const today = this.startOfDay(new Date());
    const days = Array.from({ length: CALENDAR_DAYS }, (_, i) => {
      const date = this.addDays(today, i);
      // Cifras estables por fecha: el mismo día da siempre lo mismo.
      const seed = date.getDate() * 7 + date.getMonth() * 13;
      const count = 6 + (seed % 9);
      return { date: this.iso(date), payouts_count: count, amount_cents: count * (380 + (seed % 120)) };
    });
    return { term_days: this.readTerms().term_days, days };
  }

  private respond(body: unknown, contentType = 'application/json'): Observable<HttpEvent<unknown>> {
    return of(
      new HttpResponse({ status: 200, body, headers: new HttpHeaders({ 'Content-Type': contentType }) }),
    ).pipe(delay(250 + Math.random() * 300));
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, n: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  }

  private iso(date: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
  }
}
